/**
 * rotation-swaps — pairs a rotation's diffs into a readable SWAP SCHEMA (pure).
 * For Media Devoted | Created By Ciro Autiero
 *
 * Diffs arrive as flat eliminate/requeue/promote/blocked entries. For humans
 * the story is "X was replaced by Y in pool P", so per pool we zip the outs
 * (eliminate + requeue, in diff order) with the ins (promote — the engine emits
 * them in priority order) index-wise; leftovers stay one-sided ("slot freed" /
 * "fills a free slot"). `blocked` never pairs; `keep` is ignored. Pool order =
 * first appearance in the diff list (stable).
 */

import type { FlowLayer, RotationElementDiff } from '@trafficpulse/shared';

/** A per-POOL element key: the same bank lander/offer can be a row in several
 *  path pools, and planned diffs are per pool — keying by bankId alone would
 *  paint one pool's verdict onto every row of that element. */
export function poolElementKey(e: {
  layer: FlowLayer;
  poolId: string;
  bankId: string;
}): string {
  return `${e.layer}:${e.poolId}:${e.bankId}`;
}

export interface RotationSwap {
  key: string;
  layer: FlowLayer;
  poolId: string;
  poolName: string;
  out?: RotationElementDiff;
  in?: RotationElementDiff;
}

export interface RotationSchema {
  swaps: RotationSwap[];
  blocked: RotationElementDiff[];
  /** MD-1147: offers leaving / rejoining a path for their partner cap, and a
   *  cap-removed path back in the queue — not a swap, listed on their own. */
  caps: RotationElementDiff[];
}

interface PoolBucket {
  layer: FlowLayer;
  poolId: string;
  poolName: string;
  outs: RotationElementDiff[];
  ins: RotationElementDiff[];
}

function bucketFor(
  buckets: Map<string, PoolBucket>,
  diff: RotationElementDiff,
): PoolBucket {
  const key = `${diff.layer}:${diff.poolId}`;
  const existing = buckets.get(key);
  if (existing) return existing;
  const created: PoolBucket = {
    layer: diff.layer,
    poolId: diff.poolId,
    poolName: diff.poolName,
    outs: [],
    ins: [],
  };
  buckets.set(key, created);
  return created;
}

export function buildRotationSchema(
  diffs: ReadonlyArray<RotationElementDiff>,
): RotationSchema {
  const buckets = new Map<string, PoolBucket>();
  const blocked: RotationElementDiff[] = [];
  const caps: RotationElementDiff[] = [];
  for (const diff of diffs) {
    if (diff.action === 'keep') continue;
    if (diff.action === 'blocked') {
      blocked.push(diff);
      continue;
    }
    if (diff.action === 'cap-pause' || diff.action === 'cap-reactivate') {
      caps.push(diff);
      continue;
    }
    const bucket = bucketFor(buckets, diff);
    if (diff.action === 'promote') bucket.ins.push(diff);
    else bucket.outs.push(diff);
  }
  const swaps: RotationSwap[] = [];
  for (const [key, bucket] of buckets) {
    const rows = Math.max(bucket.outs.length, bucket.ins.length);
    for (let i = 0; i < rows; i += 1) {
      swaps.push({
        key: `${key}:${i}`,
        layer: bucket.layer,
        poolId: bucket.poolId,
        poolName: bucket.poolName,
        out: bucket.outs[i],
        in: bucket.ins[i],
      });
    }
  }
  return { swaps, blocked, caps };
}

/** Swaps grouped per pool, preserving first-appearance pool order. */
export function swapsByPool(swaps: RotationSwap[]): RotationSwap[][] {
  const groups = new Map<string, RotationSwap[]>();
  for (const swap of swaps) {
    const key = `${swap.layer}:${swap.poolId}`;
    groups.set(key, [...(groups.get(key) ?? []), swap]);
  }
  return [...groups.values()];
}
